import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { fetchDocuments, uploadDocument } from '../../../../Redux/ActionCreators/onboardingAction';

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIcon = (type) => {
  const t = type || '';
  if (t.includes('pdf')) return { bg: '#fee2e2', color: '#991b1b', label: 'PDF' };
  if (t.includes('word') || t.includes('doc')) return { bg: '#dbeafe', color: '#1e40af', label: 'DOC' };
  return { bg: '#f3f4f6', color: '#6b7280', label: 'FILE' };
};

const DocumentsStep = ({ formData, update }) => {
  const dispatch = useDispatch();
  const docs = formData.documents || {};
  const set = (key, val) => update('documents', { ...docs, [key]: val });
  const fileRef = useRef();

  const [availableDocs, setAvailableDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalSelected, setModalSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadDocs = () => {
    setLoading(true);
    dispatch(fetchDocuments('org', 1, 100,)).then(res => {
      setAvailableDocs(res?.data || []);
      setLoading(false);
    });
  };

  useEffect(() => { loadDocs(); }, [dispatch]);

  const selectedDocuments = docs.selectedDocuments || [];
  const isSelected = (docId) => selectedDocuments.some(d => String(d.id) === String(docId));
  const getMode = (docId) => (selectedDocuments.find(d => String(d.id) === String(docId)) || {}).mode || 'read';

  const setMode = (docId, mode) => {
    set('selectedDocuments',
      selectedDocuments.map(d => String(d.id) === String(docId) ? { ...d, mode } : d)
    );
  };

  const removeDoc = (docId) => {
    set('selectedDocuments', selectedDocuments.filter(d => String(d.id) !== String(docId)));
  };

  const unaddedDocs = availableDocs.filter(d => !isSelected(d.id));
  const addedDocs = availableDocs.filter(d => isSelected(d.id));

  const openModal = () => {
    setModalSelected([]);
    setUploadError('');
    setShowModal(true);
  };

  const toggleModalDoc = (docId) => {
    setModalSelected(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleAdd = () => {
    const newDocs = modalSelected.map(id => ({ id, mode: 'read' }));
    set('selectedDocuments', [...selectedDocuments, ...newDocs]);
    setShowModal(false);
    setModalSelected([]);
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setUploadError('');

    let successCount = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('bucket', 'org');
      fd.append('badge', 'org');
      fd.append('detail', `${formatSize(file.size)} · uploaded to Azure Blob`);

      const result = await dispatch(uploadDocument(fd));

      if (!result?.success) {
        const detail = Array.isArray(result?.detail) ? result.detail.join(', ') : result?.detail;
        setUploadError(detail || result?.message || 'Upload failed');
      } else {
        successCount++;
      }
    }

    if (successCount > 0) {
      setSnackbar({ open: true, message: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully.`, severity: 'success' });
    }

    setUploading(false);
    dispatch(fetchDocuments('org', 1, 100,)).then(res => setAvailableDocs(res?.data || []));
  };

  return (
    <div>
      <div style={{ background: '#fff', border: '1px solid #e4e9f0', borderRadius: 12, padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>Company documents</div>
          <span style={{
            background: '#f3f4f6', color: '#6b7280', fontSize: 11, fontWeight: 500,
            padding: '3px 10px', borderRadius: 99,
          }}>
            {addedDocs.length} attached
          </span>
        </div>

        {loading ? (
          <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {addedDocs.map(doc => {
              const icon = fileIcon(doc.mimeType || doc.content_type);
              const mode = getMode(doc.id);
              const subtitle = doc.detail || (doc.size ? formatSize(doc.size) : null);

              return (
                <div
                  key={doc.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    border: '1px solid #f75891', borderRadius: 8, background: '#fdf2f8',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, background: icon.bg, color: icon.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                  }}>
                    {icon.label}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: '#1a1a2e',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {doc.name}
                    </div>
                    {subtitle && (
                      <div style={{ fontSize: 11, color: '#8a9199', marginTop: 2 }}>{subtitle}</div>
                    )}
                  </div>
                  <select
                    value={mode}
                    onChange={e => setMode(doc.id, e.target.value)}
                    style={{
                      padding: '6px 10px', fontSize: 12,
                      border: '1px solid #d0d5dd', borderRadius: 6,
                      background: '#fff', color: '#1a1a2e',
                      cursor: 'pointer', outline: 'none', flexShrink: 0, minWidth: 148,
                    }}
                  >
                    <option value="read">Read</option>
                    <option value="accept">Read &amp; Accept</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeDoc(doc.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 4, color: '#ef4444', display: 'flex', alignItems: 'center',
                    }}
                    title="Remove document"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  </button>
                </div>
              );
            })}

            {addedDocs.length === 0 && !loading && (
              <div style={{
                textAlign: 'center', padding: '24px 16px',
                border: '1.5px dashed #e4e9f0', borderRadius: 8, color: '#8a9199', fontSize: 13,
              }}>
                No documents attached yet. Click <strong>+ Add Documents</strong> to attach files to this flow.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, paddingTop: 16, borderTop: '1px solid #e4e9f0' }}>
          <button
            className="ob-btn ob-btn--primary"
            type="button"
            onClick={openModal}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Documents
          </button>
        </div>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: 28, width: 560,
            maxHeight: '82vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Add Documents</div>
                <div style={{ fontSize: 12, color: '#8a9199', marginTop: 3 }}>Upload new files or select from existing documents</div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  background: '#f3f4f6', border: 'none', borderRadius: 8,
                  width: 32, height: 32, cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 18,
                }}
              >
                ×
              </button>
            </div>

            {/* Upload zone */}
            <div
              className="ob-upload-zone"
              style={{
                borderColor: dragActive ? '#f75891' : undefined,
                background: dragActive ? 'rgba(247,88,145,0.06)' : undefined,
                marginBottom: 4,
              }}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={e => { e.preventDefault(); setDragActive(false); handleUpload(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? (
                <div className="ob-spinner" style={{ margin: 0 }}>
                  <div className="spinner-border" role="status" />
                </div>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8a9199" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              )}
              <p style={{ margin: '6px 0 2px', fontSize: 13 }}>
                {uploading ? 'Uploading…' : 'Click or drag files to upload'}
              </p>
              <span style={{ fontSize: 11, color: '#8a9199' }}>PDF, DOC, DOCX, XLS, PNG, JPG — max 20MB</span>
              <input
                ref={fileRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
              />
            </div>

            {uploadError && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 8, background: '#fef2f2',
                border: '1px solid #fecaca', color: '#dc2626', fontSize: 12, marginBottom: 8,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {uploadError}
              </div>
            )}

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#e4e9f0' }} />
              <span style={{ fontSize: 11, color: '#9aa3b0', whiteSpace: 'nowrap' }}>or choose from existing</span>
              <div style={{ flex: 1, height: 1, background: '#e4e9f0' }} />
            </div>

            {/* Document list */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {availableDocs.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '24px 16px',
                  border: '1.5px dashed #e4e9f0', borderRadius: 8, color: '#8a9199', fontSize: 13,
                }}>
                  No documents found. Upload files above to get started.
                </div>
              ) : unaddedDocs.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '24px 16px',
                  border: '1.5px dashed #e4e9f0', borderRadius: 8, color: '#8a9199', fontSize: 13,
                }}>
                  All available documents are already attached.
                </div>
              ) : (
                unaddedDocs.map(doc => {
                  const icon = fileIcon(doc.mimeType || doc.content_type);
                  const selected = modalSelected.includes(doc.id);
                  const subtitle = doc.detail || (doc.size ? formatSize(doc.size) : null);

                  return (
                    <div
                      key={doc.id}
                      onClick={() => toggleModalDoc(doc.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        border: `1px solid ${selected ? '#f75891' : '#e4e9f0'}`,
                        borderRadius: 8, background: selected ? '#fdf2f8' : '#fff',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleModalDoc(doc.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                      />
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, background: icon.bg, color: icon.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, flexShrink: 0,
                      }}>
                        {icon.label}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 600, color: '#1a1a2e',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {doc.name}
                        </div>
                        {subtitle && (
                          <div style={{ fontSize: 11, color: '#8a9199', marginTop: 2 }}>{subtitle}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex', gap: 10, justifyContent: 'flex-end',
              marginTop: 20, paddingTop: 16, borderTop: '1px solid #e4e9f0',
            }}>
              <button className="ob-btn" type="button" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button
                className="ob-btn ob-btn--primary"
                type="button"
                onClick={handleAdd}
                disabled={modalSelected.length === 0}
              >
                Add{modalSelected.length > 0 ? ` (${modalSelected.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DocumentsStep;
